import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import anthemUrl from "@assets/PadPulse_Anthem_Heartbeat_of_the_Hustle_1781972202299.mp3";

type AnthemContextValue = {
  isPlaying: boolean;
  hasInteracted: boolean;
  toggle: () => void;
  dismissed: boolean;
  dismiss: () => void;
  restore: () => void;
};

// Persisted across sessions so a host who hides the anthem prompt keeps a clean
// topbar on every future visit. Clearing this localStorage key resets it and
// the button re-appears.
const ANTHEM_DISMISSED_KEY = "padpulse-anthem-dismissed";

function readDismissed(): boolean {
  try {
    return localStorage.getItem(ANTHEM_DISMISSED_KEY) === "1";
  } catch {
    return false;
  }
}

// A safe no-op default so components that read the context outside a provider
// (e.g. isolated layout render tests) don't crash. In production the real
// provider is mounted above the router in App.tsx.
const AnthemContext = createContext<AnthemContextValue>({
  isPlaying: false,
  hasInteracted: false,
  toggle: () => {},
  dismissed: false,
  dismiss: () => {},
  restore: () => {},
});

// Owns the anthem <audio> element and playback state. It is mounted ABOVE the
// router so it never unmounts during in-app navigation — that's what lets a
// track started on the dashboard keep playing as the host moves between pages
// (each page renders its own AppLayout/topbar, so the audio cannot live there).
export function AnthemProvider({ children }: { children: ReactNode }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [dismissed, setDismissed] = useState<boolean>(readDismissed);

  useEffect(() => {
    const audio = new Audio(anthemUrl);
    // Don't fetch the MP3 until the host actually plays it.
    audio.preload = "none";
    function onEnded() {
      // The track simply finishes — it does not loop.
      setIsPlaying(false);
    }
    audio.addEventListener("ended", onEnded);
    audioRef.current = audio;
    return () => {
      audio.pause();
      audio.removeEventListener("ended", onEnded);
      audioRef.current = null;
    };
  }, []);

  function toggle() {
    const audio = audioRef.current;
    if (!audio) return;
    if (!hasInteracted) setHasInteracted(true);
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio
        .play()
        .then(() => {
          setIsPlaying(true);
        })
        .catch(() => {
          setIsPlaying(false);
        });
    }
  }

  function dismiss() {
    // Stop any in-progress playback and hide the prompt for good.
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
    }
    setIsPlaying(false);
    setDismissed(true);
    try {
      localStorage.setItem(ANTHEM_DISMISSED_KEY, "1");
    } catch {
      // Ignore storage failures — the prompt still stays hidden for the session.
    }
  }

  function restore() {
    // Bring the anthem prompt back: clear the persisted flag so the pill
    // re-appears on the dashboard now and on every future visit.
    setDismissed(false);
    try {
      localStorage.removeItem(ANTHEM_DISMISSED_KEY);
    } catch {
      // Ignore storage failures — the prompt still shows for the session.
    }
  }

  return (
    <AnthemContext.Provider value={{ isPlaying, hasInteracted, toggle, dismissed, dismiss, restore }}>
      {children}
    </AnthemContext.Provider>
  );
}

export function useAnthem(): AnthemContextValue {
  return useContext(AnthemContext);
}
